import { useMemo } from "react";

import { ProtectedLink } from "@impero-com/common/components/protectedLink";
import * as Cmp from "@impero-com/common/utils/comparison";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    TST_BLESSED_ID_COLUMN_SELECTION,
    TST_COLUMN_SIZE,
    TST_DEFAULT_PAGE_SIZE,
} from "@impero-com/ui";
import {
    usePresetTableSearchParams,
    ZodCodecMapToType,
} from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import {
    Column,
    createColumnHelper,
    getCoreRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    Row,
    useReactTable,
} from "@tanstack/react-table";
import { Checkbox } from "antd";

import {
    PushReportConfigurationFrequency,
    PushReportConfigurationForUserListDto,
    SavedReportType2,
    SavedReportViewDtoContent2,
} from "api/generated/model";
import { CustomEllipsisLabel } from "components/table/tsTable/CustomEllipsisLabel";
import { TSTValidMultipleOptionFilterSelect } from "components/table/tsTable/TSTValidMultipleOptionFilterSelect";
import { LegacyRoute, LegacyRouteFragment } from "legacyRoutes";
import { canViewSavedReport } from "utils/actions";
import { createFilterFnWithMode } from "utils/table/createFilterFnWithMode";
import { createFilterHelpersGetter } from "utils/table/getTypedFilterValue";
import { tstArrIncludesSomeFilterFn } from "utils/table/tstArrIncludesSomeFilterFn";

import {
    formatPushReportFrequency,
    formatSavedReportType,
    FREQUENCY_SORT_INDEX,
} from "../utils";

import {
    PUSH_REPORTS_RESPONSIBLE_LIST_COLUMN_IDS,
    PUSH_REPORTS_RESPONSIBLE_TABLE_STATE_STORAGE_KEY,
    pushReportsResponsibleListColumnFiltersCodecMap,
    pushReportsResponsibleListTableDefaultValues,
} from "./consts";

export interface PushReportResponsibleRow {
    id: string;
    savedReportId: string;
    customReportName: string;
    pushReportName: string;
    type: SavedReportType2 | null;
    typeLabel: string;
    frequency: PushReportConfigurationFrequency;
    frequencyLabel: string;
    recipientsCount: number;
    canViewCustomReport: boolean;
}

export function useUserPushReportsResponsibleListTable(
    data: PushReportConfigurationForUserListDto
) {
    const { t } = useTranslation();

    const tableData = useMemo<PushReportResponsibleRow[]>(() => {
        // The configuration carries only savedReportId; name, type and the view
        // permission live in linkedResources. Index once instead of per cell.
        const savedReportsById = new Map<string, SavedReportViewDtoContent2>(
            data.linkedResources.savedReports.map((dto) => [
                dto.resource.id,
                dto,
            ])
        );

        return data.content.map(({ resource }) => {
            const savedReport = savedReportsById.get(resource.savedReportId);
            return {
                id: resource.id,
                savedReportId: resource.savedReportId,
                customReportName: savedReport?.resource.title ?? "",
                pushReportName: resource.title,
                type: savedReport?.resource.type ?? null,
                typeLabel:
                    savedReport === undefined
                        ? ""
                        : formatSavedReportType(savedReport.resource.type, t),
                frequency: resource.frequency,
                frequencyLabel: formatPushReportFrequency(
                    resource.frequency,
                    t
                ),
                recipientsCount: resource.recipientsCount,
                canViewCustomReport:
                    savedReport !== undefined &&
                    canViewSavedReport(savedReport.actions),
            };
        });
    }, [data, t]);

    const stateAndOnChanges = usePresetTableSearchParams(
        PUSH_REPORTS_RESPONSIBLE_TABLE_STATE_STORAGE_KEY,
        pushReportsResponsibleListTableDefaultValues,
        pushReportsResponsibleListColumnFiltersCodecMap,
        t("Table_RemovedFiltersWarning"),
        Sentry.captureException
    );

    const table = useReactTable({
        ...stateAndOnChanges,
        data: tableData,
        columns: buildColumns(t),
        getRowId: (row) => row.id,
        initialState: {
            ...stateAndOnChanges.initialState,
            pagination: { pageSize: TST_DEFAULT_PAGE_SIZE, pageIndex: 0 },
        },
        enableSortingRemoval: false,
        getCoreRowModel: getCoreRowModel<PushReportResponsibleRow>(),
        getFilteredRowModel: getFilteredRowModel<PushReportResponsibleRow>(),
        getSortedRowModel: getSortedRowModel<PushReportResponsibleRow>(),
        getFacetedUniqueValues:
            getFacetedUniqueValues<PushReportResponsibleRow>(),
        getPaginationRowModel:
            getPaginationRowModel<PushReportResponsibleRow>(),
    });

    return {
        table,
        tableId: PUSH_REPORTS_RESPONSIBLE_TABLE_STATE_STORAGE_KEY,
        getFilterComponent: (column: Column<PushReportResponsibleRow>) =>
            getFilterComponent(t, column),
    };
}

const columnHelper = createColumnHelper<PushReportResponsibleRow>();

function buildColumns(t: TFunction) {
    return [
        columnHelper.display({
            id: TST_BLESSED_ID_COLUMN_SELECTION,
            header: ({ table }) => (
                <Checkbox
                    checked={table.getIsAllPageRowsSelected()}
                    indeterminate={table.getIsSomePageRowsSelected()}
                    onChange={table.getToggleAllRowsSelectedHandler()}
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                />
            ),
            size: TST_COLUMN_SIZE.CHECKBOX,
            meta: { fixedWidth: true },
        }),
        columnHelper.accessor((row) => row.customReportName, {
            id: PUSH_REPORTS_RESPONSIBLE_LIST_COLUMN_IDS.CUSTOM_REPORT_NAME,
            header: () => t("User_PushReports_CustomReport_Name"),
            cell: ({ row: { original: row } }) => (
                <ProtectedLink
                    href={`${LegacyRoute.REPORT_CONTROL_RESULT_STATUS_SAVED}/${row.savedReportId}/${LegacyRouteFragment.CONFIGURATIONS}`}
                    hasAccess={row.canViewCustomReport}
                >
                    {row.customReportName}
                </ProtectedLink>
            ),
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<
                PushReportResponsibleRow,
                string[]
            >((row, value) =>
                tstArrIncludesSomeFilterFn(
                    row,
                    PUSH_REPORTS_RESPONSIBLE_LIST_COLUMN_IDS.CUSTOM_REPORT_NAME,
                    value
                )
            ),
            size: TST_COLUMN_SIZE.L,
        }),
        columnHelper.accessor((row) => row.pushReportName, {
            id: PUSH_REPORTS_RESPONSIBLE_LIST_COLUMN_IDS.PUSH_REPORT_NAME,
            header: () => t("User_PushReports_Name"),
            cell: (info) => <span>{info.getValue()}</span>,
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<
                PushReportResponsibleRow,
                string[]
            >((row, value) =>
                tstArrIncludesSomeFilterFn(
                    row,
                    PUSH_REPORTS_RESPONSIBLE_LIST_COLUMN_IDS.PUSH_REPORT_NAME,
                    value
                )
            ),
            size: TST_COLUMN_SIZE.XL,
        }),
        columnHelper.accessor((row) => row.typeLabel, {
            id: PUSH_REPORTS_RESPONSIBLE_LIST_COLUMN_IDS.TYPE,
            header: () => t("Report_SavedReports_Column_Type"),
            cell: (info) => <span>{info.getValue()}</span>,
            enableSorting: true,
            sortingFn: "alphanumeric",
            enableColumnFilter: true,
            // (typeLabel) is derived from the underlying value (type).
            // The filter needs to operate on the underlying value to ensure correct filtering behavior.
            filterFn: createFilterFnWithMode<
                PushReportResponsibleRow,
                string[]
            >(
                (row, value) =>
                    row.original.type !== null &&
                    value.includes(row.original.type)
            ),
            size: TST_COLUMN_SIZE.L,
        }),
        columnHelper.accessor((row) => row.frequencyLabel, {
            id: PUSH_REPORTS_RESPONSIBLE_LIST_COLUMN_IDS.FREQUENCY,
            header: () => t("SavePushReportConfiguration_Frequency"),
            cell: (info) => <span>{info.getValue()}</span>,
            enableSorting: true,
            sortingFn: Cmp.map(
                (row: Row<PushReportResponsibleRow>) =>
                    FREQUENCY_SORT_INDEX[row.original.frequency],
                Cmp.primitive
            ),
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<
                PushReportResponsibleRow,
                string[]
            >((row, value) => value.includes(row.original.frequency)),
            size: TST_COLUMN_SIZE.L,
        }),
        columnHelper.accessor((row) => row.recipientsCount, {
            id: PUSH_REPORTS_RESPONSIBLE_LIST_COLUMN_IDS.RECIPIENTS,
            header: () => t("SavePushReportConfiguration_Recipients"),
            cell: (info) => <span>{info.getValue()}</span>,
            enableSorting: true,
            enableGlobalFilter: false,
            enableColumnFilter: false,
            size: TST_COLUMN_SIZE.M,
        }),
    ];
}

type PushReportsResponsibleListColumnFilters = ZodCodecMapToType<
    typeof pushReportsResponsibleListColumnFiltersCodecMap
>;

function getFilterComponent(
    t: TFunction,
    column: Column<PushReportResponsibleRow>
) {
    const getFilterHelpers =
        createFilterHelpersGetter<PushReportsResponsibleListColumnFilters>(
            column
        );
    const columnId = column.id as keyof PushReportsResponsibleListColumnFilters;

    switch (columnId) {
        case PUSH_REPORTS_RESPONSIBLE_LIST_COLUMN_IDS.CUSTOM_REPORT_NAME:
        case PUSH_REPORTS_RESPONSIBLE_LIST_COLUMN_IDS.PUSH_REPORT_NAME: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const names: Map<string, number> = column.getFacetedUniqueValues();
            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    value={value}
                    setFilterValue={setFilterValue}
                    options={[...names.keys()].map((name) => ({
                        label: name,
                        value: name,
                        customLabel: <CustomEllipsisLabel text={name} />,
                    }))}
                    placeholder={
                        columnId ===
                        PUSH_REPORTS_RESPONSIBLE_LIST_COLUMN_IDS.CUSTOM_REPORT_NAME
                            ? t("User_PushReports_CustomReport_Name")
                            : t("User_PushReports_Name")
                    }
                />
            );
        }
        case PUSH_REPORTS_RESPONSIBLE_LIST_COLUMN_IDS.TYPE: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const types = new Set(
                column
                    .getFacetedRowModel()
                    .flatRows.flatMap((row) =>
                        row.original.type === null ? [] : [row.original.type]
                    )
            );
            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    value={value}
                    setFilterValue={setFilterValue}
                    options={[...types].map((type) => ({
                        label: formatSavedReportType(type, t),
                        value: type,
                    }))}
                    placeholder={t("Report_SavedReports_Column_Type")}
                />
            );
        }
        case PUSH_REPORTS_RESPONSIBLE_LIST_COLUMN_IDS.FREQUENCY: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const frequencies = new Set(
                column
                    .getFacetedRowModel()
                    .flatRows.map((row) => row.original.frequency)
            );
            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    value={value}
                    setFilterValue={setFilterValue}
                    options={[...frequencies].map((frequency) => ({
                        label: formatPushReportFrequency(frequency, t),
                        value: frequency,
                    }))}
                    placeholder={t("SavePushReportConfiguration_Frequency")}
                />
            );
        }
        default:
            return assertNever(columnId);
    }
}
